import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Archive, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { appointmentAPI } from '@/lib/api';

/**
 * DeleteAppointmentDialog — soft-archive (approved w/ writing #) or hard delete.
 * Self-owns the pending state; the parent just tracks `open` and the row.
 * Phase 27.14 — switched from stale agentAPI.deleteAppointment (which no
 * longer exists) to appointmentAPI.delete/archive so the confirm actually
 * calls the correct endpoint. When the row is Approved + has a writing
 * number the backend forbids hard delete, so we route through archive.
 */
export const DeleteAppointmentDialog = ({ open, onOpenChange, appointment, agentNpn: _agentNpn, onDeleted }) => {
  const [pending, setPending] = React.useState(false);
  const isArchive = appointment?.appointment_status === 'Approved' && appointment?.writing_number;

  const handleConfirm = async () => {
    if (!appointment) return;
    setPending(true);
    try {
      if (isArchive) {
        await appointmentAPI.archive(appointment.appointment_id);
      } else {
        await appointmentAPI.delete(appointment.appointment_id);
      }
      toast.success(isArchive ? 'Appointment archived' : 'Appointment deleted');
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{isArchive ? 'Archive Appointment' : 'Delete Appointment'}</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block mb-3">
              Carrier: <strong>{appointment?.carrier_name}</strong><br />
              Status: <strong>{appointment?.appointment_status}</strong>
              {appointment?.writing_number && <><br />Writing #: <strong>{appointment.writing_number}</strong></>}
            </span>
            {isArchive
              ? <span className="text-amber-600">This approved appointment will be archived to preserve audit history.</span>
              : <span className="text-red-600">This appointment will be permanently deleted. This action cannot be undone.</span>}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={pending}
            className={isArchive ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-500 hover:bg-red-600'}
          >
            {isArchive ? <><Archive size={14} className="mr-1" /> Archive</> : <><Trash2 size={14} className="mr-1" /> Delete</>}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteAppointmentDialog;
